<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { IsoDate, Event } from "./shared";
import {
    processEvents,
    fromIso,
    toIso,
    addDays,
    formatters,
} from "./shared";
import EventCard from "./EventCard.vue";
import ThinChevroneLeftIcon from "@/js/svg/ThinChevroneLeft.vue";
import ThinChevroneRightIcon from "@/js/svg/ThinChevroneRight.vue";
import ArrowRightIcon from "@/js/svg/ArrowRight.vue";

const props = withDefaults(
    defineProps<{
        modelValue?: IsoDate;
        navigationLink?: string;
        viewAllText?: string;
        exploreEventsText?: string;
        eventsApiUrl?: string;
    }>(),
    {
        modelValue: () => toIso(new Date()),
        navigationLink: "/search",
        viewAllText: "View all events",
        exploreEventsText: "Explore our events",
        eventsApiUrl: "/api/events",
    },
);

const viewDate = ref<Date>(fromIso(props.modelValue));

const weekDays = computed(() => {
    return Array.from({ length: 7 }, (_, i) => {
        const date = addDays(viewDate.value, i);
        return {
            date,
            iso: toIso(date),
            dayLabel: formatters.dayDate.format(date),
            weekdayLabel: formatters.weekdayShort.format(date),
        };
    });
});

const currentMonthLabel = computed(() =>
    formatters.monthYear.format(viewDate.value),
);

const prevWeek = () => {
    viewDate.value = addDays(viewDate.value, -7);
};
const nextWeek = () => {
    viewDate.value = addDays(viewDate.value, 7);
};

const eventsByDate = ref<Record<string, Event[]>>({});
const isLoading = ref(false);

async function fetchEventsForRange(start: IsoDate, end: IsoDate) {
    try {
        isLoading.value = true;
        const queryParams = "?start=" + start + "&end=" + end;

        const response = await fetch(`${props.eventsApiUrl}${queryParams}`);

        if (!response.ok) {
            throw new Error('Failed to fetch events');
        }

        const data = await response.json();

        eventsByDate.value = processEvents(data.data);
    } catch (error) {
        console.error('Error fetching events:', error);
    } finally {
        isLoading.value = false;
    }
}

watch(
    viewDate,
    (newStart) => {
        const startIso = toIso(newStart);
        const endIso = toIso(addDays(newStart, 6));
        fetchEventsForRange(startIso, endIso);
    },
    { immediate: true },
);
</script>

<template>
    <div class="hidden flex-col gap-8 mx-auto px-12 py-16">
        <div class="flex items-center relative justify-start">
            <div class="flex items-center gap-4">
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="size-12 flex items-center justify-center rounded-2xl bg-misty-pine-1 hover:bg-misty-pine-2 transition-colors"
                        @click="prevWeek"
                    >
                        <ThinChevroneLeftIcon class="text-20 text-darkGreen" />
                    </button>
                    <button
                        type="button"
                        class="size-12 flex items-center justify-center rounded-2xl bg-misty-pine-1 hover:bg-misty-pine-2 transition-colors"
                        @click="nextWeek"
                    >
                        <ThinChevroneRightIcon class="text-20 text-darkGreen" />
                    </button>
                </div>
                <span
                    class="font-medium text-22 tracking-tightest text-graphite-9"
                >
                    {{ currentMonthLabel }}
                </span>
            </div>

            <h2
                class="absolute left-1/2 -translate-x-1/2 font-medium text-40 tracking-tightest text-misty-pine-5"
            >
                {{ exploreEventsText }}
            </h2>
        </div>

        <div
            class="border border-graphite-1 overflow-hidden grid grid-cols-7 divide-x divide-graphite-1 h-[33rem] bg-white relative"
        >
            <div
                v-if="isLoading"
                class="absolute inset-0 bg-white/70 z-10 flex items-center justify-center transition-opacity"
            >
                <div
                    class="w-10 h-10 border-4 border-darkGreen border-t-transparent rounded-full animate-spin"
                ></div>
            </div>

            <div
                v-for="day in weekDays"
                :key="day.iso"
                class="flex flex-col min-h-0 h-full"
            >
                <div
                    class="px-3 pt-3 flex items-center justify-between shrink-0"
                >
                    <span
                        class="font-medium text-22 tracking-tightest text-graphite-9"
                    >
                        {{ day.dayLabel }}
                    </span>
                    <span
                        class="text-graphite-6 font-medium text-22 tracking-tightest"
                    >
                        {{ day.weekdayLabel }}
                    </span>
                </div>

                <div
                    class="flex-1 custom-scrollbar overflow-y-auto p-3 flex flex-col gap-3"
                >
                    <EventCard
                        v-for="event in eventsByDate[day.iso] ?? []"
                        :key="event.event_details.id"
                        :event="event"
                    />
                    <div
                        v-if="!eventsByDate[day.iso]?.length"
                        class="h-full min-h-[50px]"
                    ></div>
                </div>
            </div>
        </div>

        <div class="flex justify-center mt-4">
            <a :href="navigationLink" class="primary-button">
                {{ viewAllText }}
                <ArrowRightIcon class="text-20 text-white" />
            </a>
        </div>
    </div>
</template>

<style>
.custom-scrollbar::-webkit-scrollbar {
    width: 12px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    margin-block: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #e0eae8;
    border-radius: 12px;
    border: 3px solid transparent;
    background-clip: content-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: #a1c0bb;
}
</style>
